import { StrictMode } from 'react';
import { createRoot } from 'react-dom/client';

/**
 * Web entry point.
 *
 * Wires the composition root in three layers, top-down:
 *   1. ThemeProvider   — supplies the `<Toaster>` portal + class toggles
 *                        for dark mode (Slice 1 feature).
 *   2. QueryClientProvider — react-query provides cache + mutation hooks
 *                        used by every feature module.
 *   3. RouterProvider  — React Router v7 in `createBrowserRouter` mode;
 *                        guards (Guest/Auth/Tenant) live in `router.tsx`.
 *
 * The providers are intentionally ordered with theme OUTERMOST so the
 * toast portal sits at the body root regardless of route nesting, and
 * with router INNERMOST so that `useNavigate()` works inside the query
 * cache's `onSuccess` handlers (which run via QueryClient, not Router).
 */
import './styles/index.css';
import { ThemeProvider } from '@/hooks/useTheme';
import { App } from './App';

/**
 * Version-skew recovery.
 *
 * When a new build is deployed, the previous hashed chunks are deleted
 * from the server. A browser tab that was opened BEFORE the deploy still
 * holds the old chunk map in memory, so navigating to any lazy route
 * tries to `import()` a hash that now 404s → React Router surfaces
 * "Unexpected Application Error! error loading dynamically imported
 * module". Vite dispatches `vite:preloadError` in that case.
 *
 * We force ONE full reload to fetch the fresh index.html + chunk map
 * (index.html is served `Cache-Control: no-cache` in prod, so the reload
 * always gets the new hashes). A short sessionStorage time-guard prevents
 * a reload loop if the chunk is genuinely missing for another reason.
 */
const RELOAD_GUARD_KEY = 'vite:preloadError:lastReload';
window.addEventListener('vite:preloadError', () => {
  const last = Number(sessionStorage.getItem(RELOAD_GUARD_KEY) ?? '0');
  if (Date.now() - last < 10_000) return;
  sessionStorage.setItem(RELOAD_GUARD_KEY, String(Date.now()));
  window.location.reload();
});

const rootEl = document.getElementById('root');
if (!rootEl) {
  throw new Error('Root element #root not found in index.html');
}

createRoot(rootEl).render(
  <StrictMode>
    <ThemeProvider>
      <App />
    </ThemeProvider>
  </StrictMode>,
);
